
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, Plus, FileText, Database, Server, Calendar, MoreVertical } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface DataIngestionProps {
  editing: boolean;
}

const mockConnectors = [
  { 
    id: '1',
    name: 'Smart Meter Data Feed',
    type: 'IoT Stream',
    format: 'JSON',
    frequency: 'Real-time',
    status: 'active',
  },
  { 
    id: '2',
    name: 'Monthly CSV Reports',
    type: 'File Import',
    format: 'CSV',
    frequency: 'Monthly',
    status: 'active',
  },
  { 
    id: '3',
    name: 'Weather API Feed',
    type: 'REST API',
    format: 'JSON',
    frequency: 'Hourly',
    status: 'inactive',
  },
];

const DataIngestion: React.FC<DataIngestionProps> = ({ editing }) => {
  const [connectors, setConnectors] = useState(mockConnectors);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredConnectors = connectors.filter(connector => 
    connector.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleConnectorStatus = (id: string) => {
    if (!editing) return;
    
    setConnectors(connectors.map(connector => 
      connector.id === id ? {
        ...connector,
        status: connector.status === 'active' ? 'inactive' : 'active'
      } : connector
    ));
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case 'IoT Stream':
        return <Server className="h-4 w-4 mr-1.5" />;
      case 'File Import':
        return <FileText className="h-4 w-4 mr-1.5" />;
      case 'REST API':
        return <Database className="h-4 w-4 mr-1.5" />;
      default:
        return <Upload className="h-4 w-4 mr-1.5" />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Data Ingestion</h3>
        
        {editing && (
          <Button size="sm">
            <Plus className="h-4 w-4 mr-1" />
            Add Data Source
          </Button>
        )}
      </div>

      <div className="flex items-center space-x-2 mb-4">
        <Input
          placeholder="Search data sources..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-xs"
        />
      </div>

      {filteredConnectors.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="hidden md:table-cell">Format</TableHead>
                  <TableHead className="hidden md:table-cell">Frequency</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredConnectors.map(connector => (
                  <TableRow key={connector.id}>
                    <TableCell className="font-medium">{connector.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        {getIconForType(connector.type)}
                        {connector.type}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{connector.format}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center">
                        <Calendar className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
                        {connector.frequency}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={`${
                          connector.status === 'active' 
                            ? 'bg-green-50 text-green-700 hover:bg-green-50 border-green-200' 
                            : 'bg-red-50 text-red-700 hover:bg-red-50 border-red-200'
                        }`}
                      >
                        {connector.status === 'active' ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" disabled={!editing}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => toggleConnectorStatus(connector.id)}>
                            {connector.status === 'active' ? 'Disable' : 'Enable'}
                          </DropdownMenuItem>
                          <DropdownMenuItem>Configure</DropdownMenuItem>
                          <DropdownMenuItem>View History</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-600">Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10 px-6 text-center">
            <Upload className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Data Sources Found</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md">
              {searchTerm ? 
                `No data sources match "${searchTerm}". Try a different search term.` : 
                'Configure data ingestion processes from multiple sources like IoT devices, APIs, and file uploads.'}
            </p>
            {editing && (
              <Button>
                <Plus className="h-4 w-4 mr-1" />
                Add First Data Source
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DataIngestion;
